import { z } from 'zod';
/**
 * Tab Git — nối kho mã (GitLab tự host, GitHub) vào công việc. docs/plan-git-integration.md.
 *
 * APM-GIT-a: chỉ **hình dữ liệu** và **hàm thuần**. Không DB, không HTTP — mọi
 * thứ ở đây chạy được ở cả trình duyệt lẫn Node, và các task sau (bộ kéo, luật
 * tự đổi trạng thái, chỉ số) gọi vào thay vì viết lại.
 *
 * APM-GIT-b thêm hình của **cấu hình**: máy chủ git của bản cài đặt (`GitHost*`,
 * §3.1) và cấu hình tab Git của một dự án (`GitProjectSettings`, §4.1).
 *
 * Bốn phần:
 * - **Cấu hình** (§3.1, §3.3, §4.1, §6.1): `GitHostView` + input,
 *   `GitRepositorySettings`, `GitRule`, `GitProjectSettings`.
 * - **Hình chuẩn hoá** (§4.2): thứ một adapter trả về, tên trường khớp cột của
 *   các bảng `git_*` để `git-mirror-store` ghi thẳng.
 * - **Dựng liên kết** (§3.3, §5.4, §8.3): `parseRepoUrl`, `extractRefs`,
 *   `buildBranchName`.
 * - **Luật và dẫn xuất theo thời gian** (§6.2, §7.1, §7.3, §8.6):
 *   `decideTransition`, `stateAt`, `cycleBreakdown`, `isSensitivePath`.
 */
// ── Máy chủ và cấu hình kho (§3) ────────────────────────────────────────────
/** Loại máy chủ git. `github` gồm cả github.com lẫn GitHub Enterprise Server. */
export const GitHostKind = z.enum(['gitlab', 'github']);
/** Chu kỳ kéo PM chọn được, phút (§3.3). Dưới 5 phút là việc của webhook. */
export const GIT_INTERVAL_MINUTES = [5, 15, 30, 60];
// ── Máy chủ git của bản cài đặt (§3.1) — APM-GIT-b ──────────────────────────
/** GitHub.com có đúng một cặp địa chỉ; nút "Thêm GitHub.com" điền sẵn và khoá lại. */
export const GITHUB_COM_API_BASE_URL = 'https://api.github.com';
export const GITHUB_COM_WEB_BASE_URL = 'https://github.com';
/** Máy chủ này là github.com (không phải GitHub Enterprise Server) — so theo địa chỉ API. */
export function isGithubDotCom(host) {
    return host.kind === 'github' && host.apiBaseUrl.replace(/\/+$/, '') === GITHUB_COM_API_BASE_URL;
}
/**
 * Địa chỉ gốc của một máy chủ git — chỉ `http(s)`, không user/pass, query hay
 * fragment; **được** có đường dẫn con (GitLab cài dưới `/gitlab`). `https://` bắt
 * buộc ở production: kiểm ở API (`NODE_ENV`), không ở đây — schema dùng chung
 * không biết môi trường.
 */
export const GitHostUrl = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .refine((raw) => {
    try {
        const url = new URL(raw);
        return ((url.protocol === 'https:' || url.protocol === 'http:') &&
            !url.username &&
            !url.password &&
            !url.search &&
            !url.hash);
    }
    catch {
        return false;
    }
}, { message: 'Địa chỉ phải là URL http(s) không có query, fragment hay thông tin đăng nhập' });
export const GIT_HOST_NAME_MAX = 80;
export const GIT_HOST_CA_PEM_MAX = 20_000;
/**
 * `git_host.config` — không bí mật, trả ra cho super admin. `caPem` là CA riêng
 * của máy chủ nội bộ; **không** có công tắc tắt kiểm chứng chỉ (§11).
 */
export const GitHostConfig = z
    .object({
    caPem: z
        .string()
        .trim()
        .min(1)
        .max(GIT_HOST_CA_PEM_MAX)
        .refine((pem) => pem.includes('-----BEGIN CERTIFICATE-----'), {
        message: 'CA phải ở dạng PEM (-----BEGIN CERTIFICATE-----)',
    }),
})
    .partial()
    .strict();
/** Hàng ở màn quản trị hệ thống (`/admin?section=git-hosts`). */
export const GitHostView = z.object({
    id: z.uuid(),
    kind: GitHostKind,
    name: z.string(),
    apiBaseUrl: z.string(),
    webBaseUrl: z.string(),
    config: GitHostConfig.nullable(),
    minIntervalMinutes: z.number().int(),
    enabled: z.boolean(),
    /** Số kết nối dự án (kể cả đã thu hồi) trỏ tới — > 0 thì không xoá được. */
    connectionCount: z.number().int(),
    /** Số tài khoản git đã gắn tay cho người ở máy chủ này — > 0 cũng không xoá được. */
    identityCount: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Lỗi hình dạng theo loại (§3.1). GitHub.com là **một cặp cố định**: địa chỉ API
 * của nó chỉ đi với đúng địa chỉ web của nó, không đi với loại `gitlab`, và không
 * nhận CA riêng — sai một vế là `parseRepoUrl` so link kho với một máy chủ khác
 * với nơi token được gửi tới.
 */
export function gitHostShapeIssues(shape) {
    const issues = [];
    const api = shape.apiBaseUrl.trim().replace(/\/+$/, '');
    const web = shape.webBaseUrl.trim().replace(/\/+$/, '');
    const apiIsDotCom = api === GITHUB_COM_API_BASE_URL;
    const webIsDotCom = web === GITHUB_COM_WEB_BASE_URL;
    if (shape.kind === 'gitlab' && (apiIsDotCom || webIsDotCom)) {
        issues.push({
            path: 'apiBaseUrl',
            message: 'Đây là địa chỉ của GitHub.com — chọn loại GitHub',
        });
    }
    if (shape.kind === 'github' && apiIsDotCom !== webIsDotCom) {
        issues.push({
            path: apiIsDotCom ? 'webBaseUrl' : 'apiBaseUrl',
            message: `GitHub.com dùng đúng cặp ${GITHUB_COM_API_BASE_URL} và ${GITHUB_COM_WEB_BASE_URL}`,
        });
    }
    if (shape.kind === 'github' && apiIsDotCom && shape.config?.caPem) {
        issues.push({ path: 'config.caPem', message: 'GitHub.com không dùng CA riêng' });
    }
    return issues;
}
/**
 * Tạo máy chủ. `webBaseUrl` bỏ trống = cùng địa chỉ với `apiBaseUrl` (GitLab và
 * GitHub Enterprise Server thường thế). `kind` không đổi được sau khi tạo.
 */
export const CreateGitHostInput = z
    .object({
    kind: GitHostKind,
    name: z.string().trim().min(1).max(GIT_HOST_NAME_MAX),
    apiBaseUrl: GitHostUrl,
    webBaseUrl: GitHostUrl.nullable().optional(),
    config: GitHostConfig.nullable().optional(),
    minIntervalMinutes: z.literal(GIT_INTERVAL_MINUTES).default(5),
    enabled: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    const shape = { ...value, webBaseUrl: value.webBaseUrl ?? value.apiBaseUrl };
    for (const issue of gitHostShapeIssues(shape)) {
        ctx.addIssue({ code: 'custom', path: issue.path.split('.'), message: issue.message });
    }
});
/** Mọi trường tuỳ chọn. Hình theo loại kiểm ở API trên **kết quả sau khi sửa** (loại nằm ở hàng). */
export const UpdateGitHostInput = z
    .object({
    name: z.string().trim().min(1).max(GIT_HOST_NAME_MAX),
    apiBaseUrl: GitHostUrl,
    webBaseUrl: GitHostUrl,
    config: GitHostConfig.nullable(),
    minIntervalMinutes: z.literal(GIT_INTERVAL_MINUTES),
    enabled: z.boolean(),
})
    .partial();
/**
 * Kết quả nút **Kiểm tra** (§3.1) — gọi **không token**: GitLab `GET
 * /api/v4/version` mong 401 JSON; GitHub `GET /meta` mong 200 JSON (GHES bật
 * private mode trả 401 JSON — cũng là "đúng địa chỉ"). Sai địa chỉ thì API trả lỗi
 * `GIT_HOST_UNREACHABLE` kèm `details.reason`, không phải `ok: false`.
 */
export const GitHostTestResult = z.object({
    ok: z.literal(true),
    upstreamStatus: z.number().int(),
    latencyMs: z.number().int(),
});
/** `details.reason` của `GIT_HOST_UNREACHABLE` — giao diện có một câu cho mỗi giá trị. */
export const GitHostUnreachableReason = z.enum([
    'timeout',
    'network',
    'tls',
    'redirect',
    'bad_response',
    'unexpected_status',
]);
// ── Cấu hình kho (§3.3) ─────────────────────────────────────────────────────
/** Tầm nhập lần đầu, ngày (§3.3). */
export const GIT_BACKFILL_DAYS = [30, 90, 365];
/**
 * `git_repository.settings` (§3.3). Mọi khoá đều có mặc định, nên
 * `GitRepositorySettings.parse({})` là cấu hình của một kho vừa gắn — và một
 * hàng ghi trước khi thêm khoá mới vẫn đọc ra đủ.
 */
export const GitRepositorySettings = z.object({
    intervalMinutes: z.literal(GIT_INTERVAL_MINUTES).default(15),
    backfillDays: z.literal(GIT_BACKFILL_DAYS).default(90),
    /** Mẫu glob của nhánh phát hành. Rỗng = chỉ nhánh mặc định. */
    releaseBranchPattern: z.string().trim().max(200).default(''),
    productionEnvironment: z.string().trim().min(1).max(100).default('production'),
    /** Bình luận link ngược trên MR (§8.4). Cần token có quyền ghi. */
    postBacklink: z.boolean().default(false),
    tagPattern: z.string().trim().min(1).max(100).default('v*'),
    /** Cho skill AI đọc diff của MR (§8.6). Tắt = chỉ siêu dữ liệu. */
    aiReadDiff: z.boolean().default(false),
});
export const DEFAULT_GIT_REPOSITORY_SETTINGS = GitRepositorySettings.parse({});
/** Mức hiển thị của kho ở máy chủ git. GitLab và GitHub dùng cùng ba mức. */
export const GitRepoVisibility = z.enum(['private', 'internal', 'public']);
// ── Trạng thái dùng chung của bản sao (§4.2) ────────────────────────────────
/** Trạng thái CI ở hình chung — bảng quy đổi từng máy chủ ở §9.2, §10.2. */
export const GitCiStatus = z.enum(['none', 'pending', 'running', 'success', 'failed', 'canceled']);
/** "MR" của GitLab, "PR" của GitHub — một tên chung. */
export const GitChangeRequestState = z.enum(['open', 'merged', 'closed']);
export const GitReviewState = z.enum(['none', 'requested', 'changes_requested', 'approved']);
export const GitDeploymentStatus = z.enum(['running', 'success', 'failed', 'canceled']);
/**
 * `closes` — việc này **được làm xong** bởi thứ kia; `mentions` — chỉ nhắc tới.
 * Chỉ `closes` mới kích được luật đổi trạng thái theo MR (§6.2 lý do 3).
 */
export const GitLinkIntent = z.enum(['mentions', 'closes']);
/** Liên kết trỏ tới task (`GOLD-12`) hay issue vận hành (`GOLD-I12`). */
export const GitLinkTargetKind = z.enum(['task', 'issue']);
/**
 * Văn bản mà `extractRefs` đọc được mã. Là bốn giá trị đầu của `GitLinkFoundIn`;
 * hai giá trị còn lại là liên kết **thừa hưởng**, không qua văn bản.
 */
export const GitRefSource = z.enum(['branch_name', 'cr_title', 'cr_description', 'commit_message']);
/** `git_link.found_in` (§4.2). */
export const GitLinkFoundIn = z.enum([...GitRefSource.options, 'via_branch', 'via_change_request']);
// ── Hình chuẩn hoá (§4.2) ───────────────────────────────────────────────────
/**
 * Một thời điểm ở dạng ISO 8601 **UTC `Z`**. GitLab trả mốc kèm offset
 * (`+07:00`) — adapter đổi bằng `new Date(x).toISOString()` trước khi dựng hình.
 */
const GitTimestamp = z.iso.datetime();
/**
 * Một kho như máy chủ git mô tả. Tên trường của mọi hình `Normalized*` khớp cột
 * của bảng `git_*` tương ứng (camelCase) — kể cả hậu tố `Ext` của mốc thời gian
 * phía nguồn, để phân biệt với `created_at` của chính hàng trong DB.
 */
export const NormalizedRepo = z.object({
    /** Id số của GitLab; id số (hoặc `node_id`) của GitHub — luôn ở dạng chuỗi. */
    externalId: z.string(),
    /** `nhom/con/kho`. */
    fullPath: z.string(),
    name: z.string(),
    webUrl: z.string(),
    /** `null` = kho rỗng, chưa có nhánh nào. */
    defaultBranch: z.string().nullable(),
    visibility: GitRepoVisibility,
});
export const NormalizedCommit = z.object({
    sha: z.string(),
    /** Dòng đầu của lời commit. */
    title: z.string(),
    /** Cả lời commit; nơi ghi cắt ở 4 KB (§11). */
    message: z.string(),
    authorName: z.string().nullable(),
    /** ⚠️ Chuỗi **tự khai** (`git config user.email`) — không dùng làm cửa quyền (§0). */
    authorEmail: z.string().nullable(),
    authoredAt: GitTimestamp,
    committedAt: GitTimestamp,
    webUrl: z.string().nullable(),
});
export const NormalizedBranch = z.object({
    name: z.string(),
    headSha: z.string(),
    isDefault: z.boolean(),
    isProtected: z.boolean(),
    isMerged: z.boolean(),
    /** GitHub không trả ngày commit ở danh sách nhánh (§10.2) ⇒ `null`. */
    lastCommitAt: GitTimestamp.nullable(),
});
export const GitReviewer = z.object({ username: z.string(), name: z.string().nullable() });
/**
 * Một MR/PR. Nửa trên là thứ **danh sách** của cả hai máy chủ đều trả; nửa dưới
 * (tuỳ chọn) cần thêm lời gọi (chi tiết, commit, review, CI) nên chỉ có khi lượt
 * kéo đã đọc. Trường **vắng mặt** = "lượt này không đọc, giữ nguyên giá trị đang
 * lưu"; có mặt và bằng `null` = "đã đọc, không có" — cùng quy ước với
 * `NormalizedTask` của bộ đồng bộ.
 *
 * `ready_at` không nằm ở đây: nó là lúc **ta thấy** MR hết nháp (§7.1), nên do
 * nơi ghi tính, không do máy chủ git trả.
 */
export const NormalizedChangeRequest = z.object({
    /** Số hiển thị (`iid` của GitLab, `number` của GitHub) — không phải id toàn cục. */
    number: z.number().int().positive(),
    title: z.string(),
    /** Nơi ghi cắt ở 16 KB (§11). */
    description: z.string().nullable(),
    state: GitChangeRequestState,
    isDraft: z.boolean(),
    sourceBranch: z.string(),
    targetBranch: z.string(),
    authorUsername: z.string().nullable(),
    authorName: z.string().nullable(),
    headSha: z.string().nullable(),
    mergeCommitSha: z.string().nullable(),
    squashCommitSha: z.string().nullable(),
    webUrl: z.string(),
    createdAtExt: GitTimestamp,
    updatedAtExt: GitTimestamp,
    mergedAt: GitTimestamp.nullable(),
    closedAt: GitTimestamp.nullable(),
    /** `authored_at` sớm nhất trong các commit của MR. */
    firstCommitAt: GitTimestamp.nullable().optional(),
    firstReviewAt: GitTimestamp.nullable().optional(),
    approvedAt: GitTimestamp.nullable().optional(),
    reviewState: GitReviewState.optional(),
    reviewers: z.array(GitReviewer).optional(),
    ciStatus: GitCiStatus.optional(),
    ciUrl: z.string().nullable().optional(),
    changedFiles: z.number().int().nonnegative().nullable().optional(),
    additions: z.number().int().nonnegative().nullable().optional(),
    deletions: z.number().int().nonnegative().nullable().optional(),
    commitCount: z.number().int().nonnegative().nullable().optional(),
});
export const NormalizedPipeline = z.object({
    externalId: z.string(),
    ref: z.string(),
    sha: z.string(),
    status: GitCiStatus,
    webUrl: z.string().nullable(),
    createdAtExt: GitTimestamp,
    finishedAt: GitTimestamp.nullable(),
    durationS: z.number().int().nonnegative().nullable(),
});
export const NormalizedRelease = z.object({
    tag: z.string(),
    name: z.string().nullable(),
    sha: z.string(),
    /** Tag không có release ⇒ ngày của commit mà tag trỏ tới. */
    releasedAt: GitTimestamp,
    webUrl: z.string().nullable(),
    isPrerelease: z.boolean(),
});
export const NormalizedDeployment = z.object({
    externalId: z.string(),
    environment: z.string(),
    ref: z.string(),
    sha: z.string(),
    status: GitDeploymentStatus,
    createdAtExt: GitTimestamp,
    finishedAt: GitTimestamp.nullable(),
    webUrl: z.string().nullable(),
});
// ── Mẫu glob và phạm vi nhánh (§3.3, §6.1) ──────────────────────────────────
function escapeRegExp(literal) {
    return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Khớp mẫu glob tối giản của `tagPattern`, `releaseBranchPattern` và
 * `branchScope`: chỉ `*` là ký tự đại diện, và nó khớp **cả `/`** (như mẫu nhánh
 * được bảo vệ của GitLab: `release/*` khớp `release/2026/q4`). Phân biệt hoa
 * thường, khớp cả chuỗi. Mẫu rỗng không khớp gì.
 */
export function matchGitGlob(pattern, value) {
    if (!pattern)
        return false;
    const source = pattern.split('*').map(escapeRegExp).join('.*');
    return new RegExp(`^${source}$`).test(value);
}
export const GIT_BRANCH_SCOPE_DEFAULT = 'default';
export const GIT_BRANCH_SCOPE_PROTECTED = 'protected';
/**
 * Phạm vi nhánh của một luật (§6.1): `default` (nhánh mặc định) · `protected`
 * (mọi nhánh được bảo vệ) · còn lại là một mẫu glob (`release/*`).
 */
export const GitBranchScope = z.string().trim().min(1).max(200);
/** Nhánh có thuộc phạm vi của luật không. Nơi gọi kiểm trước khi `decideTransition`. */
export function matchesBranchScope(scope, branch) {
    if (scope === GIT_BRANCH_SCOPE_DEFAULT)
        return branch.isDefault;
    if (scope === GIT_BRANCH_SCOPE_PROTECTED)
        return branch.isProtected;
    return matchGitGlob(scope, branch.name);
}
// ── Luật tự đổi trạng thái (§6) ─────────────────────────────────────────────
/** Sự kiện git kích được luật — bảng §6.1. */
export const GitRuleTrigger = z.enum([
    'work_started',
    'cr_opened',
    'cr_approved',
    'cr_merged',
    'deployed',
]);
/** Một luật của `git_project_settings.rules`. Mặc định **không luật nào bật** (§2 kết luận 4). */
export const GitRule = z.object({
    trigger: GitRuleTrigger,
    branchScope: GitBranchScope.default(GIT_BRANCH_SCOPE_DEFAULT),
    toStatusId: z.uuid(),
    enabled: z.boolean(),
});
/**
 * Vì sao một luật **không** chạy. Sáu giá trị đầu là sáu lý do của §6.2 và là
 * thứ ghi vào `git_rule_fired.skip_reason`. `rule_inactive` là chốt chặn cho
 * nơi gọi quên lọc (luật tắt, hoặc luật của trigger khác) — không ghi sổ.
 */
export const GitTransitionSkipReason = z.enum([
    'already_fired',
    'backward',
    'mentions_only',
    'pending_work',
    'skipped_by_marker',
    'status_missing',
    'rule_inactive',
]);
/** Khoá của sổ chống chạy lặp cho **một** task: cặp `(trigger, subject_key)` của `git_rule_fired`. */
export function gitRuleFiredKey(trigger, subjectKey) {
    return `${trigger}:${subjectKey}`;
}
const CATEGORY_RANK = { todo: 0, in_progress: 1, done: 2 };
const CLOSES_ONLY_TRIGGERS = new Set([
    'cr_opened',
    'cr_merged',
    'deployed',
]);
/**
 * Toàn bộ luật "sự kiện git có đổi trạng thái task không" (§6.2) — không viết
 * logic quyết định ở chỗ nào khác.
 *
 * Thứ tự kiểm khi nhiều lý do cùng đúng: sổ chống lặp → đích còn tồn tại →
 * thứ thuộc về **liên kết** (`[skip-pm]`, chỉ nhắc tới) → thứ thuộc về **trạng
 * thái lúc này** (đi lùi, còn MR khác đang mở). Lý do bền đứng trước lý do tạm
 * để sổ ghi lại điều không đổi theo thời gian.
 *
 * Hàm này không kiểm `branchScope` (cần biết nhánh — `matchesBranchScope`) và
 * không kiểm quyền hay sprint đã đóng: việc đó của `ChangeTaskStatusCommand`,
 * lệnh ném thì nơi gọi ghi `skipped` kèm mã lỗi.
 */
export function decideTransition(input) {
    const { rule, trigger, current } = input;
    const skip = (reason) => ({
        apply: false,
        reason,
    });
    if (!rule.enabled || rule.trigger !== trigger)
        return skip('rule_inactive');
    // 1. Người kéo task ngược lại bằng tay thì cùng sự kiện ấy không đẩy lại.
    if (input.firedKeys.has(gitRuleFiredKey(trigger, input.subjectKey)))
        return skip('already_fired');
    // 6. Cột đích đã bị xoá khỏi workflow.
    const target = input.statuses.find((status) => status.id === rule.toStatusId);
    if (!target)
        return skip('status_missing');
    // 5. MR tự xin đứng ngoài.
    if (input.skipPm)
        return skip('skipped_by_marker');
    // 3. "Ref GOLD-4" không phải lời hứa làm xong GOLD-4.
    if (input.linkIntent === 'mentions' && CLOSES_ONLY_TRIGGERS.has(trigger)) {
        return skip('mentions_only');
    }
    // 2. Chỉ đi tới: đích phải đứng **sau** chỗ task đang đứng. Cùng cột cũng là
    //    "không sau" — và vì `done` là nhóm cuối, không luật nào kéo task ra khỏi nó.
    const fromRank = CATEGORY_RANK[current.category];
    const toRank = CATEGORY_RANK[target.category];
    const forward = toRank > fromRank || (toRank === fromRank && target.position > current.position);
    if (!forward)
        return skip('backward');
    // 4. Task làm bằng ba MR thì MR thứ ba merge mới chuyển.
    if (trigger === 'cr_merged' &&
        input.otherClosingChangeRequests.some((other) => other.state === 'open')) {
        return skip('pending_work');
    }
    return { apply: true, toStatusId: target.id };
}
/** `git@host:nhom/kho.git` — dạng scp của SSH, không phải URL. */
const SCP_LIKE = /^[\w.-]+@([^\s/:]+):(.+)$/;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
const REPO_PATH_SEGMENT = /^[A-Za-z0-9_.-]+$/;
const MAX_REPO_FULL_PATH = 255;
function locateRepo(raw) {
    if (!HAS_SCHEME.test(raw)) {
        const scp = SCP_LIKE.exec(raw);
        if (scp)
            return { hostname: scp[1].toLowerCase(), port: null, path: scp[2] };
    }
    let url;
    try {
        // Dán thiếu giao thức (`git.congty.vn/nhom/kho`) vẫn đọc được.
        url = new URL(HAS_SCHEME.test(raw) ? raw : `https://${raw}`);
    }
    catch {
        return null;
    }
    const hostname = url.hostname.toLowerCase();
    if (!hostname)
        return null;
    if (url.protocol === 'https:' || url.protocol === 'http:') {
        return { hostname, port: url.port, path: url.pathname };
    }
    if (url.protocol === 'ssh:' || url.protocol === 'git+ssh:') {
        return { hostname, port: null, path: url.pathname };
    }
    return null;
}
function repoFullPath(kind, path) {
    let segments;
    try {
        segments = path
            .split('/')
            .filter((segment) => segment !== '')
            .map((segment) => decodeURIComponent(segment));
    }
    catch {
        return null;
    }
    if (kind === 'gitlab') {
        // `/nhom/kho/-/merge_requests/5`: mọi trang con của GitLab nằm sau `/-/`.
        const dash = segments.indexOf('-');
        if (dash !== -1)
            segments = segments.slice(0, dash);
    }
    else {
        // GitHub luôn là `owner/repo`; phần sau (`/pull/5`, `/tree/main`) là trang con.
        segments = segments.slice(0, 2);
    }
    if (segments.length < 2)
        return null;
    const last = segments.length - 1;
    segments[last] = segments[last].replace(/\.git$/i, '');
    const valid = segments.every((segment) => REPO_PATH_SEGMENT.test(segment) && segment !== '.' && segment !== '..');
    if (!valid)
        return null;
    const fullPath = segments.join('/');
    return fullPath.length <= MAX_REPO_FULL_PATH ? fullPath : null;
}
/**
 * Đọc link kho người dùng dán (§3.3) và tìm máy chủ git đã khai chứa nó.
 *
 * Nhận: `https://host/nhom/kho`, có hoặc không `.git`, có `/` cuối, link tới
 * một trang con (`…/-/merge_requests/5`, `…/pull/5`), `git@host:nhom/kho.git`,
 * `ssh://git@host:2222/nhom/kho.git`, và máy chủ cài dưới đường dẫn con
 * (`https://congty.vn/gitlab/nhom/kho` với `webBaseUrl = https://congty.vn/gitlab`).
 *
 * So máy chủ theo **hostname + cổng** của `webBaseUrl`, không theo giao thức.
 * Link SSH chỉ so hostname. Nhiều máy chủ cùng hostname ⇒ lấy máy chủ có đường
 * dẫn con dài nhất còn khớp.
 *
 * ⚠️ Chỉ đọc **chuỗi**. Kho có tồn tại không, token có thấy không là việc của
 * adapter — và URL máy chủ luôn lấy từ `git_host`, không bao giờ từ chuỗi người
 * dùng dán (SSRF, §11).
 */
export function parseRepoUrl(url, hosts) {
    const matched = matchHostOf(url, hosts);
    if (!matched.ok)
        return matched;
    const fullPath = repoFullPath(matched.host.kind, matched.rest);
    if (!fullPath)
        return { ok: false, reason: 'invalid_url' };
    return { ok: true, hostId: matched.host.id, kind: matched.host.kind, fullPath };
}
/**
 * Link nhóm / organization người dùng dán (APM-GIT-s, §16.6) — cùng cách so máy
 * chủ với `parseRepoUrl`.
 *
 * GitLab: `https://host/nhom`, `…/nhom/con`, `…/groups/nhom/con` (trang con sau
 * `/-/` bị bỏ). GitHub: `https://github.com/ten-org`, `…/orgs/ten-org` (phần sau
 * bị bỏ — `…/ten-org/kho` đọc ra organization `ten-org`).
 */
export function parseGroupUrl(url, hosts) {
    const matched = matchHostOf(url, hosts);
    if (!matched.ok)
        return matched;
    const fullPath = groupFullPath(matched.host.kind, matched.rest);
    if (!fullPath)
        return { ok: false, reason: 'invalid_url' };
    return { ok: true, hostId: matched.host.id, kind: matched.host.kind, fullPath };
}
/**
 * Đường dẫn nhóm người dùng gõ (không phải link): `nhom/con` · `ten-org`. Bỏ `/`
 * hai đầu; `null` = không phải đường dẫn nhóm hợp lệ của loại máy chủ ấy.
 */
export function normalizeGroupPath(kind, path) {
    return groupFullPath(kind, path);
}
function groupFullPath(kind, path) {
    let segments;
    try {
        segments = path
            .split('/')
            .filter((segment) => segment !== '')
            .map((segment) => decodeURIComponent(segment));
    }
    catch {
        return null;
    }
    if (kind === 'gitlab') {
        if (segments[0] === 'groups')
            segments = segments.slice(1);
        const dash = segments.indexOf('-');
        if (dash !== -1)
            segments = segments.slice(0, dash);
    }
    else {
        if (segments[0] === 'orgs')
            segments = segments.slice(1);
        segments = segments.slice(0, 1);
    }
    if (segments.length < 1)
        return null;
    const valid = segments.every((segment) => REPO_PATH_SEGMENT.test(segment) && segment !== '.' && segment !== '..');
    if (!valid)
        return null;
    const fullPath = segments.join('/');
    return fullPath.length <= MAX_REPO_FULL_PATH ? fullPath : null;
}
/** Máy chủ đã khai chứa link, và phần đường dẫn **sau** gốc của máy chủ ấy. */
function matchHostOf(url, hosts) {
    const location = locateRepo(url.trim());
    if (!location)
        return { ok: false, reason: 'invalid_url' };
    let best = null;
    for (const host of hosts) {
        let base;
        try {
            base = new URL(host.webBaseUrl);
        }
        catch {
            continue;
        }
        if (base.hostname.toLowerCase() !== location.hostname)
            continue;
        const prefix = base.pathname.replace(/\/+$/, '');
        let rest = location.path;
        if (location.port !== null) {
            if (base.port !== location.port)
                continue;
            if (prefix && rest !== prefix && !rest.startsWith(`${prefix}/`))
                continue;
            rest = rest.slice(prefix.length);
        }
        if (!best || prefix.length > best.prefixLength) {
            best = { host, rest, prefixLength: prefix.length };
        }
    }
    if (!best)
        return { ok: false, reason: 'host_unknown' };
    return { ok: true, host: best.host, rest: best.rest };
}
const SKIP_PM_MARKER = /\[skip-pm\]/i;
const CLOSING_KEYWORDS = 'close[sd]?|fix(?:e[sd])?|resolve[sd]?|đóng|sửa|xong';
const NON_CLOSING_KEYWORDS = 'refs?|references|part[ \\t]+of|related[ \\t]+to|see|liên[ \\t]+quan|thuộc';
/**
 * Từ khoá đứng **ngay trước** mã, cùng dòng, cho phép `:`. Biên trái bắt buộc:
 * `prefix GOLD-1` và `hotfix GOLD-1` không phải `fix GOLD-1`.
 */
const KEYWORD_BEFORE_REF = new RegExp(`(?<![\\p{L}\\p{N}_])(?:(${CLOSING_KEYWORDS})|(?:${NON_CLOSING_KEYWORDS}))[ \\t]*:?[ \\t]*$`, 'iu');
/** Giữa hai mã của cùng một danh sách: `,` · `và` · `and` · `, và` · `, and`. */
const REF_LIST_SEPARATOR = /^[ \t]*(?:,[ \t]*(?:và|and)?|và|and)[ \t]*$/iu;
/**
 * Tìm mã task / issue của **đúng một dự án** trong một đoạn văn bản của git và
 * xếp ý định cho từng mã — bảng §5.4.
 *
 * - Tên nhánh, tiêu đề MR: mọi mã là `closes`.
 * - Mô tả MR, lời commit: `closes` khi mã đứng sau từ khoá đóng (`Closes`,
 *   `Fixes`, `Resolves`, `Đóng`, `Sửa`, `Xong`) hoặc nối tiếp một mã `closes`
 *   bằng `,` / `và` / `and`; còn lại (`Ref`, `Part of`, đứng trần…) là `mentions`.
 *
 * Biểu thức tìm mã có **biên hai đầu** và escape khoá dự án, không phân biệt hoa
 * thường (tên nhánh thường viết thường): với khoá `GOLD` thì `XGOLD-1`,
 * `GOLD-1a`, `UTF-8`, `SHA-256` đều không khớp.
 *
 * ⚠️ Hàm này không biết mã có **tồn tại** không. Mã trỏ tới số không có thì nơi
 * gọi không tạo `git_link` và ghi vào danh sách "mã không tồn tại" (§7.2).
 */
export function extractRefs(projectKey, text, foundIn) {
    // NFC: văn bản gõ từ macOS hay ở dạng tách dấu, và `sửa` tách dấu không khớp từ khoá.
    const body = text.normalize('NFC');
    const skipPm = (foundIn === 'cr_title' || foundIn === 'cr_description') && SKIP_PM_MARKER.test(body);
    const key = projectKey.trim().toUpperCase();
    if (!key)
        return { refs: [], skipPm };
    const pattern = new RegExp(`(?<![A-Za-z0-9])${escapeRegExp(key)}-(I)?(\\d{1,9})(?![A-Za-z0-9])`, 'gi');
    const alwaysCloses = foundIn === 'branch_name' || foundIn === 'cr_title';
    const byCode = new Map();
    let previous = null;
    for (const match of body.matchAll(pattern)) {
        const start = match.index;
        const end = start + match[0].length;
        let intent;
        if (alwaysCloses) {
            intent = 'closes';
        }
        else if (previous && REF_LIST_SEPARATOR.test(body.slice(previous.end, start))) {
            intent = previous.intent;
        }
        else {
            const lineStart = body.lastIndexOf('\n', start - 1) + 1;
            const keyword = KEYWORD_BEFORE_REF.exec(body.slice(lineStart, start));
            intent = keyword?.[1] ? 'closes' : 'mentions';
        }
        previous = { end, intent };
        const kind = match[1] ? 'issue' : 'task';
        const number = Number(match[2]);
        const code = `${key}-${kind === 'issue' ? 'I' : ''}${number}`;
        const seen = byCode.get(code);
        if (!seen)
            byCode.set(code, { kind, number, intent, code });
        else if (intent === 'closes')
            seen.intent = 'closes';
    }
    return { refs: [...byCode.values()], skipPm };
}
// ── Tên nhánh (§8.3) ────────────────────────────────────────────────────────
export const DEFAULT_GIT_BRANCH_FORMAT = '{type}/{key}-{number}-{slug}';
export const GIT_BRANCH_FORMAT_MAX = 120;
/** Trần của `{slug}`. Tên nhánh dài hơn thế chỉ khó gõ, không dễ đọc hơn. */
export const GIT_BRANCH_SLUG_MAX = 40;
/** `{type}` của việc không có trong bảng ánh xạ. */
export const DEFAULT_GIT_BRANCH_TYPE = 'feature';
/**
 * Bảng ánh xạ mặc định `slug loại việc ⇒ {type}` — PM sửa được. `loi` là slug
 * của loại "Lỗi" trong danh mục mặc định.
 */
export const DEFAULT_GIT_BRANCH_TYPE_MAP = {
    bug: 'fix',
    loi: 'fix',
};
const BRANCH_PLACEHOLDER = /\{([^{}]*)\}/g;
const BRANCH_PLACEHOLDER_NAMES = new Set(['type', 'key', 'number', 'slug']);
/** Phần chữ cố định của định dạng: tập con an toàn của tên ref hợp lệ trong git. */
const BRANCH_LITERAL = /^[A-Za-z0-9._/-]*$/;
export const GitBranchFormatIssue = z.enum([
    'empty',
    'too_long',
    'unknown_placeholder',
    'missing_key_number',
    'invalid_characters',
]);
/**
 * Định dạng tên nhánh có dùng được không. Buộc chứa **`{key}-{number}`** liền
 * nhau: thiếu thì nhánh tạo ra không tự nối được vào task (§8.3).
 */
export function validateBranchFormat(format) {
    const fail = (reason) => ({ ok: false, reason });
    const template = format.trim();
    if (!template)
        return fail('empty');
    if (template.length > GIT_BRANCH_FORMAT_MAX)
        return fail('too_long');
    for (const match of template.matchAll(BRANCH_PLACEHOLDER)) {
        if (!BRANCH_PLACEHOLDER_NAMES.has(match[1]))
            return fail('unknown_placeholder');
    }
    if (!template.includes('{key}-{number}'))
        return fail('missing_key_number');
    // Thay chỗ giữ chỗ bằng một chữ để kiểm phần còn lại như một tên ref.
    const literal = template.replace(BRANCH_PLACEHOLDER, 'x');
    const wellFormed = BRANCH_LITERAL.test(literal) &&
        !/^[/.-]/.test(literal) &&
        !/[/.]$/.test(literal) &&
        !/\/\/|\.\.|\/\./.test(literal) &&
        !literal.endsWith('.lock');
    return wellFormed ? { ok: true } : fail('invalid_characters');
}
/** Định dạng tên nhánh của `git_project_settings.branch_format`. */
export const GitBranchFormat = z
    .string()
    .trim()
    .min(1)
    .max(GIT_BRANCH_FORMAT_MAX)
    .refine((format) => validateBranchFormat(format).ok, {
    message: 'Định dạng tên nhánh phải chứa {key}-{number} và chỉ gồm chữ, số, ". _ / -"',
});
// ── Cấu hình tab Git của một dự án (§4.1) — APM-GIT-b ───────────────────────
/** Trần số luật của một dự án: năm trigger × vài phạm vi nhánh; nhiều hơn là cấu hình không ai đọc nổi. */
export const GIT_RULES_MAX = 20;
/** Trần số dòng của bảng `loại việc ⇒ {type}`. */
export const GIT_BRANCH_TYPE_MAP_MAX = 50;
/**
 * Bảng `slug loại việc ⇒ {type}` của tên nhánh (§8.3). Giá trị là một đoạn tên
 * ref viết thường (`fix`, `feature`, `chore`) — `buildBranchName` vẫn chạy nó qua
 * `slugifyBranchPart`, schema chỉ chặn sớm thứ chắc chắn không dùng được.
 */
export const GitBranchTypeMap = z
    .record(z.string().trim().min(1).max(60), z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, {
    message: 'Chỉ chữ thường, số và dấu gạch nối',
}))
    .refine((map) => Object.keys(map).length <= GIT_BRANCH_TYPE_MAP_MAX, {
    message: `Tối đa ${GIT_BRANCH_TYPE_MAP_MAX} loại việc`,
});
/**
 * `git_project_settings` — một hàng mỗi dự án. Mọi khoá có mặc định, nên
 * `GitProjectSettings.parse({})` là cấu hình của một dự án chưa ai đụng tới:
 * định dạng nhánh mặc định, **không luật nào** (§2 kết luận 4).
 *
 * Hai luật cùng `trigger` + `branchScope` bị từ chối: chúng trả lời hai cách cho
 * cùng một sự kiện, và thứ tự trong mảng không phải thứ PM nhìn thấy.
 */
export const GitProjectSettings = z
    .object({
    branchFormat: GitBranchFormat.default(DEFAULT_GIT_BRANCH_FORMAT),
    branchTypeMap: GitBranchTypeMap.default({ ...DEFAULT_GIT_BRANCH_TYPE_MAP }),
    rules: z.array(GitRule).max(GIT_RULES_MAX).default([]),
    /**
     * Gắn cập nhật tiến độ với commit — APM-PCOM-a (docs/plan-progress-commits.md
     * §2.1). Ghi qua lệnh riêng, **không** qua `SaveGitProjectSettingsInput`.
     * Tuỳ chọn (vắng = tắt), để các màn chỉ gửi định dạng nhánh / luật không phải
     * mang theo nó.
     */
    requireProgressCommits: z.boolean().optional(),
    /**
     * AI rà commit đã báo có khớp mục / việc không — APM-PCRV
     * (docs/plan-progress-commit-review.md §2.1). Công tắc con của
     * `requireProgressCommits`, cùng lệnh riêng; vắng = tắt.
     */
    reviewProgressCommits: z.boolean().optional(),
})
    .superRefine((value, ctx) => {
    const seen = new Set();
    value.rules.forEach((rule, index) => {
        const key = `${rule.trigger}\u0000${rule.branchScope}`;
        if (seen.has(key)) {
            ctx.addIssue({
                code: 'custom',
                path: ['rules', index, 'branchScope'],
                message: 'Đã có một luật khác cho cùng sự kiện và cùng phạm vi nhánh',
            });
        }
        seen.add(key);
    });
});
export const DEFAULT_GIT_PROJECT_SETTINGS = GitProjectSettings.parse({});
/**
 * Phần chữ của tên nhánh: bỏ dấu tiếng Việt, chỉ còn `[a-z0-9-]`, không dài quá
 * `max`, không bắt đầu hay kết thúc bằng `-`. Cắt ở ranh giới từ khi còn giữ
 * được quá nửa độ dài. Tên toàn ký tự lạ (emoji, chữ Hán) ⇒ chuỗi rỗng.
 *
 * `đ`/`Đ` xử lý riêng: nó là ký tự độc lập trong Unicode, không phải `d` + dấu,
 * nên NFD không tách.
 */
export function slugifyBranchPart(input, max = GIT_BRANCH_SLUG_MAX) {
    const slug = input
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[đĐ]/g, 'd')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    if (slug.length <= max)
        return slug;
    const cut = slug.slice(0, max);
    const lastDash = cut.lastIndexOf('-');
    const atWordEnd = slug[max] === '-';
    const tidy = !atWordEnd && lastDash >= max / 2 ? cut.slice(0, lastDash) : cut;
    return tidy.replace(/-+$/g, '');
}
function branchTypeOf(subject, typeMap) {
    const slug = subject.typeSlug?.toLowerCase();
    const mapped = slug && Object.hasOwn(typeMap, slug) ? typeMap[slug] : undefined;
    // Issue vận hành là thứ đang hỏng cần sửa ⇒ `fix` khi bảng ánh xạ không nói gì.
    const fallback = subject.kind === 'issue' ? 'fix' : DEFAULT_GIT_BRANCH_TYPE;
    return slugifyBranchPart(mapped ?? fallback, 20) || DEFAULT_GIT_BRANCH_TYPE;
}
/**
 * Tên nhánh cho một việc (§8.3) — **đường làm tay** của cả tính năng: không cần
 * token, không cần kho, dự án chưa nối gì vẫn có nút "Sao chép tên nhánh".
 *
 * `{type}/{key}-{number}-{slug}` ⇒ `feature/gold-123-sua-loi-dang-nhap`.
 *
 * Định dạng không hợp lệ (`validateBranchFormat`) ⇒ dùng định dạng mặc định
 * thay vì ném: kết quả **luôn** chứa mã việc, nên nhánh tạo từ đây luôn tự nối
 * được. Tên việc không ra được chữ nào ⇒ bỏ luôn dấu nối thừa
 * (`feature/gold-123`, không phải `feature/gold-123-`).
 */
export function buildBranchName(format, task, typeMap = DEFAULT_GIT_BRANCH_TYPE_MAP) {
    const template = validateBranchFormat(format).ok ? format.trim() : DEFAULT_GIT_BRANCH_FORMAT;
    const values = {
        type: branchTypeOf(task, typeMap),
        key: task.projectKey.trim().toLowerCase(),
        number: `${task.kind === 'issue' ? 'i' : ''}${task.number}`,
        slug: slugifyBranchPart(task.title),
    };
    return template
        .replace(BRANCH_PLACEHOLDER, (_whole, name) => values[name] ?? '')
        .replace(/-{2,}/g, '-')
        .replace(/[-._]+(?=\/|$)/g, '')
        .replace(/(^|\/)[-._]+/g, '$1')
        .replace(/\/{2,}/g, '/')
        .replace(/^\/+|\/+$/g, '');
}
function instantMs(value) {
    if (value === null || value === undefined)
        return null;
    const ms = value instanceof Date ? value.getTime() : Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
}
/**
 * Trạng thái của một MR **tại `asOf`** (luật #7, §7.3), dẫn xuất từ mốc thời
 * gian của chính nó — không cần `event_log`. `null` = lúc đó MR chưa tồn tại.
 *
 * Câu SQL tương đương ("MR đang mở lúc T") phải viết cùng một phép:
 * `created_at_ext <= T AND (state = 'open' OR COALESCE(merged_at, closed_at) > T)`.
 *
 * ⚠️ Thứ **không** dựng lại được, vì máy chủ git chỉ giữ mốc cuối:
 * - MR từng đóng rồi **mở lại**: khoảng thời gian nó đóng hiện là "mở". Vì thế
 *   MR đang mở thì mở ở mọi `asOf` sau ngày tạo, kể cả khi `closedAt` còn sót
 *   lại từ lần đóng trước.
 * - MR đóng, mở lại rồi mới merge/đóng hẳn: trước mốc cuối đều hiện là "mở".
 * - Nháp hay không, tiêu đề, nhánh đích tại `asOf`: luôn là giá trị hiện tại.
 */
export function stateAt(changeRequest, asOf) {
    const at = instantMs(asOf);
    if (at === null)
        throw new RangeError('stateAt: asOf is not a valid instant');
    const created = instantMs(changeRequest.createdAtExt);
    if (created === null || created > at)
        return null;
    if (changeRequest.state === 'open')
        return 'open';
    const merged = instantMs(changeRequest.mergedAt);
    const closed = instantMs(changeRequest.closedAt);
    // GitHub đặt cả `closed_at` cho PR đã merge; GitLab chỉ đặt `merged_at`.
    const ended = changeRequest.state === 'merged' ? (merged ?? closed) : closed;
    // Thiếu mốc kết thúc thì không biết nó kết thúc lúc nào — giữ trạng thái hiện tại.
    return ended === null || ended <= at ? changeRequest.state : 'open';
}
/**
 * Thời gian chu trình của một MR chia bốn pha (§7.1).
 *
 * - `readyAt` trống và MR **không** nháp ⇒ lấy ngày tạo MR (chưa từng nháp).
 *   Trống và MR đang nháp ⇒ pha Viết chưa kết thúc.
 * - MR merge mà **không ai review** (hoặc lượt review đầu đến sau khi merge) ⇒
 *   `pickupMs` và `reviewMs` đều `null`: nó không có thời gian chờ nhận để đưa
 *   vào trung vị, và ghi 0 sẽ kéo trung vị xuống bằng những MR bỏ qua review.
 * - Mốc lệch thứ tự (review khi MR còn nháp, commit viết lại ngày) ⇒ pha đó là
 *   0, không âm.
 *
 * `deployedAt` là **ước lượng** của nơi gọi (§7.1): deployment `success` đầu
 * tiên lên production chứa `merge_commit_sha`, hoặc đầu tiên sau `merged_at`.
 */
export function cycleBreakdown(changeRequest, deployedAt) {
    const firstCommit = instantMs(changeRequest.firstCommitAt);
    const merged = instantMs(changeRequest.mergedAt);
    const deployed = merged === null ? null : instantMs(deployedAt);
    const ready = instantMs(changeRequest.readyAt) ??
        (changeRequest.isDraft ? null : instantMs(changeRequest.createdAtExt));
    const review = instantMs(changeRequest.firstReviewAt);
    const firstReview = review !== null && merged !== null && review > merged ? null : review;
    const span = (from, to) => from === null || to === null ? null : Math.max(0, to - from);
    return {
        codingMs: span(firstCommit, ready),
        pickupMs: span(ready, firstReview),
        reviewMs: span(firstReview, merged),
        deployMs: span(merged, deployed),
        leadTimeMs: span(firstCommit, deployed),
    };
}
// ── Đường dẫn không gửi nội dung cho AI (§8.6) ──────────────────────────────
function normalizePath(path) {
    const full = path.replace(/\\/g, '/').toLowerCase();
    const segments = full.split('/').filter((segment) => segment !== '');
    return { full, base: segments.at(-1) ?? '', segments };
}
const SENSITIVE_EXTENSIONS = /\.(?:pem|key|p12|pfx|jks|keystore|tfvars|kdbx|env)$/;
const SENSITIVE_BASENAME = /^(?:\.env|id_(?:rsa|dsa|ecdsa|ed25519)|\.npmrc$|\.netrc$|\.pgpass$)/;
const SENSITIVE_ANYWHERE = /secret|credential/;
/**
 * Tệp **không bao giờ gửi nội dung** cho nhà cung cấp AI, chỉ gửi đường dẫn
 * (§8.6): `.env*`, `*.pem`, `*.key`, `*.p12`, `id_rsa*`, `*secret*`,
 * `*credential*`, `*.tfvars`, `*.kdbx` — cộng vài thứ cùng loại (`*.pfx`,
 * `*.jks`, `*.keystore`, `*.env`, khoá SSH kiểu khác, `.npmrc`, `.netrc`,
 * `.pgpass`).
 *
 * `secret` / `credential` khớp ở **bất kỳ đâu** trong đường dẫn, kể cả tên thư
 * mục: chặn nhầm một tệp mã có chữ "secret" trong tên chỉ làm bản tóm tắt kém
 * đi một chút; để lọt một tệp bí mật thì không lấy lại được. Thêm mẫu vào đây
 * thì cứ thêm; **bớt** đi là mở rộng Ngoại lệ 5 của luật #3 — hỏi trước.
 */
export function isSensitivePath(path) {
    const { full, base } = normalizePath(path);
    return (SENSITIVE_BASENAME.test(base) ||
        SENSITIVE_EXTENSIONS.test(base) ||
        SENSITIVE_ANYWHERE.test(full));
}
const LOCKFILE_BASENAMES = new Set([
    'package-lock.json',
    'npm-shrinkwrap.json',
    'pnpm-lock.yaml',
    'yarn.lock',
    'bun.lockb',
    'composer.lock',
    'gemfile.lock',
    'cargo.lock',
    'poetry.lock',
    'pipfile.lock',
    'uv.lock',
    'go.sum',
]);
const GENERATED_DIRECTORIES = new Set([
    'dist',
    'build',
    'vendor',
    'node_modules',
]);
const BINARY_EXTENSIONS = /\.(?:png|jpe?g|gif|webp|bmp|ico|tiff?|avif|heic|psd|pdf|zip|gz|tgz|bz2|xz|7z|rar|tar|jar|war|exe|dll|so|dylib|bin|class|o|a|wasm|woff2?|ttf|otf|eot|mp[34]|mov|avi|mkv|webm|wav|ogg|flac|docx?|xlsx?|pptx?|sqlite|db)$/;
/**
 * Tệp **vô ích với model** (§8.6): lockfile, `*.min.*`, source map, thứ nằm
 * dưới `dist/` · `build/` · `vendor/` · `node_modules/`, ảnh và nhị phân. Gửi
 * đường dẫn, bỏ nội dung — chúng ăn hết trần 256 KB của một MR mà không nói gì.
 *
 * Tệp máy chủ git đánh dấu "sinh tự động" (`generated_file`) là cờ của API,
 * không đọc được từ đường dẫn — nơi gọi tự gộp vào.
 */
export function isLowValueDiffPath(path) {
    const { base, segments } = normalizePath(path);
    return (LOCKFILE_BASENAMES.has(base) ||
        base.endsWith('.lock') ||
        /\.min\./.test(base) ||
        base.endsWith('.map') ||
        BINARY_EXTENSIONS.test(base) ||
        segments.slice(0, -1).some((segment) => GENERATED_DIRECTORIES.has(segment)));
}
// ── Kết nối, kho và bản sao — hình API của tab (APM-GIT-d; §3.2, §3.3, §5, §7.3) ──
/** `git_connection.status` (§3.2). `invalid` = máy chủ git trả 401 cho token ⇒ kho của kết nối dừng kéo. */
export const GitConnectionStatus = z.enum(['ok', 'invalid', 'expired']);
export const GitWebhookMode = z.enum(['none', 'manual', 'auto']);
/** Thứ ở git mà một liên kết trỏ tới. `subjectRef` = tên nhánh · sha · số MR ở dạng chuỗi. */
export const GitLinkSubjectKind = z.enum(['branch', 'commit', 'change_request']);
export const GitLinkOverrideAction = z.enum(['add', 'hide']);
export const GIT_CONNECTION_LABEL_MAX = 80;
export const GIT_TOKEN_MIN = 8;
export const GIT_TOKEN_MAX = 1000;
/**
 * Token PM dán (§3.2). Chỉ đi trong **body**, không bao giờ quay lại trong phản
 * hồi (API chỉ trả `tokenHint`) và không vào event.
 */
const GitToken = z
    .string()
    .trim()
    .min(GIT_TOKEN_MIN)
    .max(GIT_TOKEN_MAX)
    .regex(/^\S+$/, { message: 'Token không chứa khoảng trắng' });
/**
 * Máy chủ git đang bật, ở mức PM cần để tạo kết nối: tên và loại. Địa chỉ API và
 * CA là việc của super admin (`GitHostView`) — không ra tới đây.
 */
export const GitHostOption = z.object({
    id: z.uuid(),
    kind: GitHostKind,
    name: z.string(),
    webBaseUrl: z.string(),
});
export const GitConnectionView = z.object({
    id: z.uuid(),
    hostId: z.uuid(),
    hostKind: GitHostKind,
    hostName: z.string(),
    /** Super admin tắt máy chủ ⇒ mọi kho của kết nối dừng kéo, không xoá gì. */
    hostEnabled: z.boolean(),
    label: z.string(),
    /** Bốn ký tự cuối của token — thứ duy nhất của token mà API trả. */
    tokenHint: z.string(),
    /** Tài khoản đứng tên token; ghi ngược (§8.4) chạy dưới tên này. */
    accountName: z.string().nullable(),
    canWrite: z.boolean(),
    canAdminHooks: z.boolean(),
    tokenExpiresAt: z.iso.datetime().nullable(),
    status: GitConnectionStatus,
    /** Số kho **đang gắn** dùng kết nối này. */
    repositoryCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable(),
});
/** `POST /projects/:projectId/git/connections` — API gọi `verify` trước khi lưu. */
export const CreateGitConnectionInput = z.object({
    hostId: z.uuid(),
    label: z.string().trim().min(1).max(GIT_CONNECTION_LABEL_MAX),
    token: GitToken,
});
/** `PUT …/connections/:connectionId/token` — dán token mới; kết nối `invalid` trở lại `ok`. */
export const RotateGitTokenInput = z.object({ token: GitToken });
/** `GET …/connections/:connectionId/remote-repositories` — kho mà token thấy (§3.3). */
export const GitRemoteRepositoriesQuery = z.object({
    search: z.string().trim().max(100).optional(),
    /** Chuỗi mờ lấy từ `nextCursor` của trang trước. */
    cursor: z.string().max(2000).optional(),
});
export const GitRemoteRepository = NormalizedRepo.extend({
    /** Kho này đã gắn vào **dự án đang xem** rồi. */
    linked: z.boolean(),
});
export const GitRemoteRepositoryPage = z.object({
    items: z.array(GitRemoteRepository),
    nextCursor: z.string().nullable(),
});
/**
 * Sửa `git_repository.settings` — chỉ khoá có mặt mới đổi.
 *
 * Khai lại từng khoá **không mặc định** thay vì `GitRepositorySettings.partial()`:
 * Zod 4 áp `.default()` cả bên trong trường tuỳ chọn, nên bản `partial()` sẽ điền
 * mặc định cho mọi khoá vắng mặt — một lần sửa `postBacklink` thành ra đặt lại cả
 * chu kỳ kéo.
 */
export const GitRepositorySettingsPatch = z
    .object({
    intervalMinutes: z.literal(GIT_INTERVAL_MINUTES),
    backfillDays: z.literal(GIT_BACKFILL_DAYS),
    releaseBranchPattern: z.string().trim().max(200),
    productionEnvironment: z.string().trim().min(1).max(100),
    postBacklink: z.boolean(),
    tagPattern: z.string().trim().min(1).max(100),
    aiReadDiff: z.boolean(),
})
    .partial();
/**
 * `POST …/git/repositories` — gắn kho (§3.3), **đúng một** trong hai cách:
 *
 * - `url`: dán link kho. Máy chủ tìm bằng `parseRepoUrl`; `connectionId` bỏ trống
 *   thì dùng kết nối còn dùng được của dự án tới đúng máy chủ ấy.
 * - `fullPath` (+ `externalId` nếu có): chọn từ danh sách kho token thấy — cần
 *   `connectionId`.
 */
export const LinkGitRepositoryInput = z
    .object({
    connectionId: z.uuid().optional(),
    url: z.string().trim().min(1).max(1000).optional(),
    fullPath: z.string().trim().min(1).max(255).optional(),
    externalId: z.string().trim().min(1).max(64).optional(),
    settings: GitRepositorySettingsPatch.optional(),
})
    .superRefine((value, ctx) => {
    if ((value.url === undefined) === (value.fullPath === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['url'],
            message: 'Dán link kho hoặc chọn một kho trong danh sách',
        });
    }
    if (value.fullPath !== undefined && value.connectionId === undefined) {
        ctx.addIssue({ code: 'custom', path: ['connectionId'], message: 'Chọn kết nối' });
    }
});
/** Tiến độ của **một luồng** trong lần nhập đầu (§5.1): đã đọc bao nhiêu, xong chưa. */
export const GitSyncStreamProgress = z.object({
    done: z.boolean(),
    count: z.number().int().nonnegative(),
});
/**
 * `git_repo_state.initial_progress` — nằm trong bảng, không trong RAM (§11).
 * Khoá của `streams` là tên luồng (`change_requests`, `commits`, `branches`, và
 * các luồng task sau thêm).
 */
export const GitInitialProgress = z.object({
    /** Mép trái của tầm nhập (`now − backfillDays` lúc bắt đầu) — cũng là `historyFrom`. */
    since: z.iso.datetime(),
    streams: z.record(z.string(), GitSyncStreamProgress),
});
/**
 * Tình trạng kéo của một kho, đọc từ `git_repo_state`.
 *
 * ⚠️ Không nhận asOf: đây là trạng thái **vận hành** lúc này của bộ kéo (đang
 * chạy không, lỗi gì), không phải một đại lượng của dự án theo thời gian.
 */
export const GitRepoSyncView = z.object({
    running: z.boolean(),
    nextRunAt: z.iso.datetime().nullable(),
    lastRunAt: z.iso.datetime().nullable(),
    lastSuccessAt: z.iso.datetime().nullable(),
    /** Mã lỗi (`GIT_*`) của lượt gần nhất; `null` = lượt gần nhất không lỗi. */
    lastErrorCode: z.string().nullable(),
    lastErrorMessage: z.string().nullable(),
    consecutiveFailures: z.number().int().nonnegative(),
    /** `null` = còn đang nhập lần đầu — xem `initialProgress`. */
    initialDoneAt: z.iso.datetime().nullable(),
    initialProgress: GitInitialProgress.nullable(),
    /** Mốc sớm nhất có dữ liệu của kho này (§7.3). */
    historyFrom: z.iso.datetime().nullable(),
    apiCallsLastRun: z.number().int().nonnegative(),
    serverVersion: z.string().nullable(),
    webhookLastSeenAt: z.iso.datetime().nullable(),
});
export const GitRepositoryView = z.object({
    id: z.uuid(),
    connectionId: z.uuid(),
    connectionLabel: z.string(),
    connectionStatus: GitConnectionStatus,
    /** Quyền ghi hiệu lực của kết nối (scope của token **và** vai trên kho lúc gắn). */
    canWrite: z.boolean(),
    hostId: z.uuid(),
    hostKind: GitHostKind,
    hostName: z.string(),
    hostEnabled: z.boolean(),
    externalId: z.string(),
    fullPath: z.string(),
    name: z.string(),
    webUrl: z.string(),
    defaultBranch: z.string().nullable(),
    visibility: GitRepoVisibility,
    settings: GitRepositorySettings,
    webhookMode: GitWebhookMode,
    /** Khác `null` = kho đã gỡ; bản sao của nó vẫn còn. */
    archivedAt: z.iso.datetime().nullable(),
    /**
     * Nhóm mà kho được gắn **qua** (APM-GIT-s, plan §16.2); `null` = gắn tay. Tuỳ chọn
     * để client cũ / fixture không phải khai.
     */
    groupId: z.uuid().nullable().optional(),
    createdAt: z.iso.datetime(),
    /** `null` = chưa có hàng trạng thái (kho vừa gắn, lượt đầu chưa chạy). */
    sync: GitRepoSyncView.nullable(),
    /**
     * Lượt CI **mới nhất** của nhánh mặc định (APM-GIT-i) — chấm CI trên thẻ kho;
     * `null` = kho chưa có lượt CI nào trong bản sao. Trạng thái lúc này, như `sync`.
     * Chỉ `GET …/git/repositories` điền trường này; phản hồi của các lệnh ghi (gắn
     * kho, đổi cấu hình) để vắng.
     */
    defaultBranchCi: z
        .object({
        status: GitCiStatus,
        sha: z.string(),
        webUrl: z.string().nullable(),
        createdAtExt: z.iso.datetime(),
        finishedAt: z.iso.datetime().nullable(),
    })
        .nullable()
        .optional(),
});
/** `POST …/repositories/:repositoryId/sync` — "Kéo ngay". `queued: false` = đã có lượt đang chờ / đang chạy. */
export const GitSyncRequestResult = z.object({ queued: z.boolean() });
// ── Đọc bản sao theo thời gian (§7.3 — luật #7) ─────────────────────────────
/**
 * Mốc của một phản hồi đọc. `partial: true` ⇔ `asOf` sớm hơn `historyFrom`: bản
 * sao không có đủ dữ liệu của lúc ấy, giao diện nói "chưa có dữ liệu git trước
 * dd/MM/yyyy" thay vì vẽ một khoảng trống trông như "không ai làm gì".
 *
 * `historyFrom` của một phản hồi gộp nhiều kho là mốc **muộn nhất** trong các kho
 * đã có dữ liệu — trước mốc ấy ít nhất một kho còn thiếu.
 */
export const GitTimeMeta = z.object({
    /** `null` = đọc "bây giờ". */
    asOf: z.iso.datetime().nullable(),
    historyFrom: z.iso.datetime().nullable(),
    partial: z.boolean(),
});
/**
 * Một việc (task / issue) gắn với một thứ ở git — chip mã trên bảng MR, nhánh,
 * commit. Chỉ mang **mã**: tên việc đọc ở chính màn của việc đó, theo quyền của nó.
 */
export const GitLinkedTarget = z.object({
    kind: GitLinkTargetKind,
    id: z.uuid(),
    /** `GOLD-123` · `GOLD-I12`. */
    code: z.string(),
    intent: GitLinkIntent,
    /** `null` = người gắn tay (`git_link_override.add`). */
    foundIn: GitLinkFoundIn.nullable(),
    manual: z.boolean(),
});
/**
 * Một MR/PR như tab hiển thị.
 *
 * `state` là trạng thái **tại `asOf`** (`stateAt`). ⚠️ Thứ không dựng lại được —
 * luôn là giá trị hiện tại dù đọc quá khứ: tiêu đề, nháp hay không, nhánh đích,
 * trạng thái review, CI, số commit / tệp đổi, và khoảng thời gian một MR từng
 * đóng rồi mở lại (máy chủ git chỉ giữ mốc cuối).
 */
export const GitChangeRequestView = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    number: z.number().int().positive(),
    title: z.string(),
    state: GitChangeRequestState,
    isDraft: z.boolean(),
    sourceBranch: z.string(),
    targetBranch: z.string(),
    authorUsername: z.string().nullable(),
    authorName: z.string().nullable(),
    webUrl: z.string(),
    createdAtExt: z.iso.datetime(),
    updatedAtExt: z.iso.datetime(),
    mergedAt: z.iso.datetime().nullable(),
    closedAt: z.iso.datetime().nullable(),
    reviewState: GitReviewState,
    reviewers: z.array(GitReviewer),
    ciStatus: GitCiStatus,
    ciUrl: z.string().nullable(),
    commitCount: z.number().int().nonnegative().nullable(),
    changedFiles: z.number().int().nonnegative().nullable(),
    additions: z.number().int().nonnegative().nullable(),
    deletions: z.number().int().nonnegative().nullable(),
    /** Việc có liên kết **hiệu lực** tới MR này (§4.2). */
    targets: z.array(GitLinkedTarget),
});
export const GIT_LIST_LIMIT_DEFAULT = 30;
export const GIT_LIST_LIMIT_MAX = 100;
const GitListLimit = z.coerce.number().int().min(1).max(GIT_LIST_LIMIT_MAX);
/** `GET …/git/change-requests`. `state` lọc theo trạng thái **tại `asOf`**. */
export const GitChangeRequestListQuery = z.object({
    repositoryId: z.uuid().optional(),
    state: z.enum(['all', ...GitChangeRequestState.options]).default('all'),
    /** Có / không có liên kết hiệu lực tới một việc. */
    linked: z.enum(['all', 'linked', 'unlinked']).default('all'),
    cursor: z.string().max(200).optional(),
    limit: GitListLimit.default(GIT_LIST_LIMIT_DEFAULT),
    asOf: z.iso.datetime().optional(),
});
export const GitChangeRequestPage = GitTimeMeta.extend({
    items: z.array(GitChangeRequestView),
    nextCursor: z.string().nullable(),
});
export const GitIdentitySource = z.enum(['manual', 'email']);
/** Người của ChillPM đứng sau một tài khoản git — gắn tay, hoặc khớp email lúc đọc (§4.1). */
export const GitPersonRef = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    source: GitIdentitySource,
});
/** Một commit trong bản sao. Không mang email tác giả (chuỗi tự khai — §0) và không mang cả lời commit. */
export const GitCommitView = z.object({
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    sha: z.string(),
    title: z.string(),
    authorName: z.string().nullable(),
    /** Người của ChillPM đứng sau email tác giả — gắn tay / khớp email (APM-GIT-u); `null` khi không biết. */
    author: GitPersonRef.nullable(),
    authoredAt: z.iso.datetime(),
    committedAt: z.iso.datetime(),
    webUrl: z.string().nullable(),
    onDefaultBranch: z.boolean(),
    targets: z.array(GitLinkedTarget),
});
/** `?asOf=` của các endpoint đọc một bản ghi. */
export const GitAsOfQuery = z.object({ asOf: z.iso.datetime().optional() });
/**
 * `GET …/repositories/:repositoryId/change-requests/:number` — ngăn bên của bảng MR:
 * mô tả, commit, và các mốc của bốn pha (§7.1 — `cycleBreakdown` tính ở web).
 * Đọc với `asOf` trước ngày tạo MR ⇒ 404.
 */
export const GitChangeRequestDetail = GitTimeMeta.extend({
    changeRequest: GitChangeRequestView.extend({
        /** Văn bản **không tin cậy** từ git — hiển thị qua bộ lọc markdown, không `innerHTML` (§11). */
        description: z.string().nullable(),
        headSha: z.string().nullable(),
        mergeCommitSha: z.string().nullable(),
        squashCommitSha: z.string().nullable(),
        firstCommitAt: z.iso.datetime().nullable(),
        readyAt: z.iso.datetime().nullable(),
        firstReviewAt: z.iso.datetime().nullable(),
        approvedAt: z.iso.datetime().nullable(),
    }),
    /** Commit của MR có `committedAt ≤ asOf`, mới nhất trước. */
    commits: z.array(GitCommitView),
});
/**
 * Một nhánh. Tồn tại tại `asOf` ⇔ `firstSeenAt ≤ asOf` và (`deletedAt` trống hoặc
 * `> asOf`). ⚠️ `firstSeenAt` của nhánh có sẵn lúc nhập lần đầu là **ước lượng**
 * (ngày commit cuối; nhánh mặc định: mép trái tầm nhập) — máy chủ git không giữ
 * ngày tạo nhánh. `headSha`, `isMerged` luôn là giá trị hiện tại.
 */
export const GitBranchView = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    name: z.string(),
    headSha: z.string(),
    isDefault: z.boolean(),
    isProtected: z.boolean(),
    isMerged: z.boolean(),
    lastCommitAt: z.iso.datetime().nullable(),
    firstSeenAt: z.iso.datetime(),
    deletedAt: z.iso.datetime().nullable(),
    /** MR mới nhất có nhánh này là nhánh nguồn (đã tồn tại tại `asOf`). */
    changeRequest: z
        .object({
        number: z.number().int().positive(),
        state: GitChangeRequestState,
        webUrl: z.string(),
    })
        .nullable(),
    targets: z.array(GitLinkedTarget),
});
export const GitBranchListQuery = z.object({
    repositoryId: z.uuid().optional(),
    asOf: z.iso.datetime().optional(),
});
export const GitBranchList = GitTimeMeta.extend({ items: z.array(GitBranchView) });
/** `GET …/git/commits` — commit **nhánh mặc định**, mới nhất trước, `committedAt ≤ asOf`. */
export const GitCommitListQuery = z.object({
    repositoryId: z.uuid().optional(),
    cursor: z.string().max(200).optional(),
    limit: GitListLimit.default(GIT_LIST_LIMIT_DEFAULT),
    asOf: z.iso.datetime().optional(),
});
export const GitCommitPage = GitTimeMeta.extend({
    items: z.array(GitCommitView),
    nextCursor: z.string().nullable(),
});
// ── Liên kết hiệu lực và quyết định của người (§4.1, §4.2, §8.2) ────────────
/** Một quyết định của người về một liên kết: gắn tay, hoặc ẩn một liên kết nhầm. */
export const GitLinkOverrideView = z.object({
    id: z.uuid(),
    targetKind: GitLinkTargetKind,
    targetId: z.uuid(),
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    subjectKind: GitLinkSubjectKind,
    subjectRef: z.string(),
    action: GitLinkOverrideAction,
    createdBy: z.uuid(),
    createdAt: z.iso.datetime(),
});
/**
 * `POST …/git/link-overrides` (`git:act`). Một quyết định cho mỗi cặp (việc, thứ ở
 * git): gửi lại cùng cặp với `action` khác là **đổi** quyết định. `subjectRef`:
 * tên nhánh · sha đầy đủ · số MR. Thứ được trỏ tới phải đã có trong bản sao.
 */
export const AddGitLinkOverrideInput = z.object({
    targetKind: GitLinkTargetKind,
    targetId: z.uuid(),
    repositoryId: z.uuid(),
    subjectKind: GitLinkSubjectKind,
    subjectRef: z.string().trim().min(1).max(255),
    action: GitLinkOverrideAction,
});
/** `GET …/git/links?targetKind=&targetId=&asOf=` — khối "Phát triển" của một việc (§8.2). */
export const GitTargetLinksQuery = z.object({
    targetKind: GitLinkTargetKind,
    targetId: z.uuid(),
    asOf: z.iso.datetime().optional(),
});
/** Số commit trả kèm trong `GitTargetLinks.commits` — `commitCount` mới là tổng. */
export const GIT_TARGET_LINK_COMMITS_MAX = 50;
/**
 * Liên kết **hiệu lực** của một task / issue (§4.2):
 * `git_link` ∪ `git_link_override(add)` − `git_link_override(hide)`.
 *
 * Tại `asOf`: chỉ thứ đã tồn tại lúc ấy (MR theo ngày tạo, commit theo ngày
 * commit, nhánh theo `firstSeenAt`/`deletedAt`) và quyết định của người đã có lúc
 * ấy. ⚠️ Một quyết định đã bị **gỡ** thì không còn ở mốc nào (hàng bị xoá; lịch sử
 * của nó nằm ở event `task.git_link_*`).
 */
export const GitTargetLinks = GitTimeMeta.extend({
    targetKind: GitLinkTargetKind,
    targetId: z.uuid(),
    branches: z.array(GitBranchView),
    changeRequests: z.array(GitChangeRequestView),
    /** Tổng số commit có liên kết hiệu lực. */
    commitCount: z.number().int().nonnegative(),
    /** Tối đa `GIT_TARGET_LINK_COMMITS_MAX` commit mới nhất. */
    commits: z.array(GitCommitView),
    /** Quyết định hiện có của người trên việc này — để giao diện cho gỡ / bỏ ẩn. */
    overrides: z.array(GitLinkOverrideView),
    /**
     * Bản phát hành chứa việc này (APM-GIT-i, §5.5 / §8.2 — chip "có trong `v1.4.0`"),
     * cũ trước, `releasedAt ≤ asOf`. ⚠️ Tập việc của một bản là kết quả lần tính gần
     * nhất, không dựng lại theo `asOf`.
     */
    releases: z
        .array(z.object({
        id: z.uuid(),
        repositoryId: z.uuid(),
        repositoryPath: z.string(),
        tag: z.string(),
        name: z.string().nullable(),
        releasedAt: z.iso.datetime(),
        webUrl: z.string().nullable(),
    }))
        .optional(),
    /**
     * Việc này "đã lên production" lúc nào (APM-GIT-i, §7.1 — **ước lượng**, xem
     * `estimateDeployment` ở `git-release.ts`): mốc muộn nhất trong các MR `closes`
     * **đã merge** của việc, và chỉ khi **mọi** MR ấy đều đã lên; `null` = chưa
     * (hoặc việc không có MR `closes` nào đã merge). MR chỉ `mentions` không tính.
     */
    deployedAt: z.iso.datetime().nullable().optional(),
});
//# sourceMappingURL=git.js.map